/**
 * 음식 카테고리 데이터 (대분류 → 중분류 → 세부 메뉴)
 * 한국 직장인 점심 식문화를 기준으로 구성했습니다.
 * ID는 표시 이름과 별도로 관리되며, 메뉴 ID는 "대분류.중분류.번호" 형식으로 자동 생성됩니다.
 *
 * 중분류의 네 번째 값(decidesVenue)이 true면 그 분류를 고른 순간 가게가 정해져 3단계에서 게임이 끝납니다.
 */
import type { SelectOption } from '../types/room';

export interface MenuItem extends SelectOption {}

export interface SubCategory extends SelectOption {
  /**
   * true면 이 중분류를 고른 시점에 "어느 가게에 갈지"가 정해지므로 게임이 3단계에서 끝납니다.
   * (예: 만두/딤섬, 돈카츠, 파스타 → 가게에 가서 각자 주문)
   * false면 메뉴에 따라 가는 가게가 달라지므로 4단계(세부 메뉴)까지 진행합니다.
   * (예: 찌개/탕 → 김치찌개집과 감자탕집은 다른 가게)
   */
  decidesVenue: boolean;
  menus: MenuItem[];
}

export interface MainCategory extends SelectOption {
  subCategories: SubCategory[];
}

/** 1단계: 식사 방식 */
export const DINING_OPTIONS: SelectOption[] = [
  { id: 'delivery', name: '배달해서 먹기', emoji: '🛵', description: '편하게 사무실에서!' },
  { id: 'dine-out', name: '나가서 먹기', emoji: '🚶', description: '밖에서 맛있게!' },
];

type SubSpec = [id: string, name: string, emoji: string, decidesVenue: boolean, menus: string[]];

function category(id: string, name: string, emoji: string, description: string, subs: SubSpec[]): MainCategory {
  return {
    id,
    name,
    emoji,
    description,
    subCategories: subs.map(([subId, subName, subEmoji, decidesVenue, menus]) => ({
      id: `${id}.${subId}`,
      name: subName,
      emoji: subEmoji,
      decidesVenue,
      menus: menus.map((menuName, index) => ({
        id: `${id}.${subId}.${index + 1}`,
        name: menuName,
        emoji: subEmoji,
      })),
    })),
  };
}

export const MAIN_CATEGORIES: MainCategory[] = [
  category('korean', '한식', '🍚', '든든한 집밥 느낌', [
    ['stew', '찌개 / 탕', '🍲', false, ['김치찌개', '된장찌개', '순두부찌개', '부대찌개', '청국장', '동태찌개', '감자탕', '해물탕', '알탕', '갈비탕']],
    ['gukbap', '국밥 / 해장국', '🥣', false, ['돼지국밥', '순대국밥', '뼈해장국', '콩나물국밥', '소머리국밥', '선지해장국', '황태해장국', '설렁탕', '곰탕', '육개장']],
    ['baekban', '백반 / 정식', '🍱', true, ['집밥 백반', '제육볶음 정식', '생선구이 정식', '고등어조림 정식', '불고기 정식', '보쌈 정식', '쌈밥 정식', '두루치기 정식']],
    ['grill', '구이 / 볶음', '🥩', false, ['삼겹살', '제육볶음', '오징어볶음', '닭갈비', '쭈꾸미볶음', '돼지불백', '돼지갈비', '소불고기', '낙지볶음']],
    ['noodle', '면 / 국수', '🍜', false, ['잔치국수', '비빔국수', '칼국수', '수제비', '냉면', '콩국수', '막국수', '들깨칼국수']],
    ['rice', '비빔밥 / 덮밥', '🥘', true, ['비빔밥', '돌솥비빔밥', '제육덮밥', '오징어덮밥', '김치볶음밥', '불고기덮밥', '낙지덮밥', '산채비빔밥']],
    ['braise', '찜 / 조림', '🍖', false, ['갈비찜', '아귀찜', '닭볶음탕', '안동찜닭', '고등어조림', '코다리조림', '해물찜', '돼지갈비찜']],
    ['other', '기타 한식', '🥟', false, ['보쌈', '족발', '삼계탕', '추어탕', '닭한마리', '떡갈비', '해물파전', '간장게장']],
  ]),
  category('chinese', '중식', '🥡', '짜장이냐 짬뽕이냐', [
    ['noodle', '짜장 / 짬뽕', '🍜', true, ['짜장면', '짬뽕', '간짜장', '삼선짬뽕', '쟁반짜장', '볶음짬뽕', '울면', '유니짜장']],
    ['rice', '볶음밥 / 덮밥', '🍚', true, ['볶음밥', '삼선볶음밥', '잡채밥', '마파두부밥', '짬뽕밥', '유산슬밥', '고추잡채밥', '새우볶음밥']],
    ['mala', '마라 요리', '🌶️', true, ['마라탕', '마라샹궈', '마라훠궈', '마라반', '마라 볶음면']],
    ['dish', '요리류', '🍤', true, ['탕수육', '깐풍기', '라조기', '깐쇼새우', '유린기', '고추잡채', '팔보채', '꿔바로우']],
    ['dumpling', '만두 / 딤섬', '🥟', true, ['군만두', '찐만두', '물만두', '딤섬', '샤오롱바오', '왕만두', '새우만두']],
    ['set', '중식 정식', '🍱', true, ['짜장면 + 탕수육 세트', '짬뽕 + 탕수육 세트', '볶음밥 + 군만두 세트', '중식 런치 코스', '중식 덮밥 세트']],
    ['other', '기타 중식', '🍢', false, ['양꼬치', '완탕면', '지삼선', '어향가지', '토마토 계란볶음', '중국식 냉면']],
  ]),
  category('japanese', '일식', '🍣', '깔끔하고 정갈하게', [
    ['tonkatsu', '돈카츠', '🍖', true, ['등심 돈카츠', '안심 돈카츠', '치즈 돈카츠', '카츠나베', '치킨카츠', '생선카츠', '왕돈까스']],
    ['ramen', '라멘 / 우동', '🍜', true, ['돈코츠 라멘', '쇼유 라멘', '미소 라멘', '가케 우동', '튀김 우동', '카레 우동', '자루 우동', '소바']],
    ['sushi', '초밥 / 회', '🍣', true, ['모둠 초밥', '연어 초밥', '광어회', '참치회', '회덮밥', '모둠 사시미', '롤 초밥', '유부초밥']],
    ['donburi', '덮밥', '🍚', true, ['규동', '카츠동', '오야코동', '연어 덮밥', '텐동', '부타동', '스테이크 덮밥', '장어 덮밥']],
    ['curry', '카레', '🍛', true, ['돈카츠 카레', '치킨 카레', '야채 카레', '비프 카레', '새우튀김 카레', '치즈 카레']],
    ['set', '정식', '🍱', true, ['삼치구이 정식', '고등어구이 정식', '연어구이 정식', '가라아게 정식', '규카츠 정식', '스키야키 정식', '벤또 도시락', '나베 정식']],
    ['other', '기타 일식', '🍢', false, ['오코노미야키', '야키소바', '타코야키', '오니기리', '가라아게', '샤브샤브', '교자', '가츠산도']],
  ]),
  category('western', '양식', '🍝', '분위기 있게 한 끼', [
    ['pasta', '파스타', '🍝', true, ['토마토 파스타', '크림 파스타', '알리오 올리오', '봉골레', '까르보나라', '로제 파스타', '라자냐', '뇨끼']],
    ['pizza', '피자', '🍕', true, ['페퍼로니 피자', '마르게리타', '고르곤졸라 피자', '콤비네이션 피자', '불고기 피자', '하와이안 피자', '포테이토 피자', '시카고 피자']],
    ['steak', '스테이크 / 그릴', '🥩', true, ['등심 스테이크', '안심 스테이크', '함박스테이크', '치킨 스테이크', '폭립', '립아이 스테이크', '그릴드 치킨']],
    ['risotto', '리조또 / 필라프', '🍚', true, ['버섯 리조또', '해산물 리조또', '크림 리조또', '토마토 리조또', '새우 필라프', '치킨 필라프']],
    ['brunch', '브런치 / 샌드위치', '🥪', true, ['에그 베네딕트', '프렌치 토스트', '클럽 샌드위치', '파니니', 'BLT 샌드위치', '아보카도 토스트', '오믈렛', '베이글 샌드위치']],
    ['other', '기타 양식', '🍽️', false, ['경양식 돈가스', '생선가스', '비프 스튜', '치즈 그라탕', '양식 런치 코스', '빠에야']],
  ]),
  category('bunsik', '분식', '🍢', '매콤달콤 분식 타임', [
    ['tteokbokki', '떡볶이', '🌶️', true, ['국물 떡볶이', '로제 떡볶이', '치즈 떡볶이', '즉석 떡볶이', '짜장 떡볶이', '마라 떡볶이', '라볶이', '쌀 떡볶이']],
    ['gimbap', '김밥', '🍙', true, ['원조 김밥', '참치 김밥', '치즈 김밥', '김치 김밥', '돈가스 김밥', '야채 김밥', '소고기 김밥', '계란말이 김밥']],
    ['noodle', '라면 / 국수', '🍜', true, ['라면', '치즈 라면', '떡만두 라면', '쫄면', '분식 우동', '비빔라면', '열라면']],
    ['fried', '튀김 / 순대', '🍤', true, ['모둠 튀김', '오징어 튀김', '김말이', '순대', '순대볶음', '고구마 튀김', '야채 튀김', '핫바']],
    ['rice', '돈가스 / 덮밥', '🍛', true, ['분식 돈가스', '오므라이스', '카레라이스', '참치마요 덮밥', '치킨마요 덮밥', '계란 볶음밥']],
    ['other', '기타 분식', '🥟', false, ['고기만두', '김치만두', '길거리 토스트', '컵밥', '어묵탕', '계란말이', '주먹밥']],
  ]),
  category('asian', '아시안', '🌏', '이국적인 향신료의 맛', [
    ['vietnamese', '베트남', '🇻🇳', true, ['쌀국수', '분짜', '반미', '월남쌈', '볶음 쌀국수', '비빔 쌀국수', '짜조', '반쎄오']],
    ['thai', '태국', '🇹🇭', true, ['팟타이', '똠얌꿍', '그린 커리', '푸팟퐁커리', '카오팟', '팟카파오', '쏨땀', '마사만 커리']],
    ['indian', '인도', '🇮🇳', true, ['버터 치킨 커리', '탄두리 치킨', '난 + 커리 세트', '비리야니', '팔락 파니르', '치킨 티카 마살라', '사모사', '달 커리']],
    ['other', '기타 아시안', '🍛', false, ['나시고렝', '미고렝', '락사', '하이난 치킨라이스', '사테', '카오소이', '바쿠테']],
  ]),
  category('mexican', '멕시칸 / 남미', '🌮', '화끈한 라틴의 맛', [
    ['taco', '타코', '🌮', true, ['비프 타코', '치킨 타코', '새우 타코', '포크 타코', '피쉬 타코', '베지 타코']],
    ['burrito', '부리토 / 퀘사디아', '🌯', true, ['비프 부리토', '치킨 부리토', '부리토 볼', '치즈 퀘사디아', '치킨 퀘사디아', '엔칠라다', '파히타']],
    ['other', '기타 남미', '🍖', false, ['나초', '브라질 슈하스코', '엠파나다', '세비체', '칠리 콘 카르네', '아사도']],
  ]),
  category('fastfood', '패스트푸드', '🍔', '빠르고 간편하게', [
    ['burger', '햄버거', '🍔', true, ['치즈버거', '불고기 버거', '치킨 버거', '새우 버거', '베이컨 치즈버거', '수제 버거', '더블 패티 버거', '머쉬룸 버거']],
    ['chicken', '치킨', '🍗', true, ['후라이드 치킨', '양념 치킨', '반반 치킨', '간장 치킨', '순살 치킨', '치킨 텐더', '시즈닝 치킨', '핫윙']],
    ['sandwich', '샌드위치 / 서브', '🥖', true, ['에그마요 샌드위치', '참치 샌드위치', '로스트 치킨 샌드위치', '햄치즈 샌드위치', '베이컨 샌드위치', '불고기 샌드위치', '랩 샌드위치']],
    ['other', '기타 패스트푸드', '🍟', false, ['핫도그', '치킨 너겟', '피시 앤 칩스', '치킨 랩', '콘도그', '감자튀김 & 콜라']],
  ]),
  category('healthy', '샐러드 / 건강식', '🥗', '가볍고 건강하게', [
    ['salad', '샐러드', '🥗', true, ['시저 샐러드', '닭가슴살 샐러드', '연어 샐러드', '리코타 치즈 샐러드', '그릭 샐러드', '콥 샐러드', '쉬림프 샐러드', '스테이크 샐러드']],
    ['poke', '포케 / 보울', '🥙', true, ['연어 포케', '참치 포케', '새우 포케', '두부 포케', '치킨 그레인볼', '퀴노아 볼', '아보카도 볼']],
    ['light', '가벼운 식사', '🍠', false, ['닭가슴살 도시락', '샐러드 파스타', '통밀 샌드위치', '고구마 & 계란 세트', '오트밀 볼', '두부 덮밥', '곤약 비빔면']],
    ['soup', '수프 / 주스', '🥤', false, ['토마토 수프', '단호박 수프', '야채 수프', '그린 주스 & 베이글', '아사이볼', '요거트 볼', '프로틴 쉐이크 세트']],
  ]),
  category('etc', '기타', '🍽️', '색다른 선택', [
    ['convenience', '편의점 / 간편식', '🏪', true, ['편의점 도시락', '삼각김밥 & 컵라면', '편의점 샌드위치', '편의점 샐러드', '냉동 볶음밥', '햇반 & 즉석국', '편의점 파스타']],
    ['bakery', '간식 / 빵', '🥐', true, ['베이커리 세트', '크로와상 & 커피', '베이글', '와플', '도넛', '소금빵 세트', '샌드위치 & 라떼']],
    ['buffet', '뷔페 / 한상', '🍱', false, ['한식 뷔페', '샐러드 뷔페', '구내식당', '초밥 뷔페', '고기 뷔페', '브런치 뷔페']],
    ['special', '특별식', '🎉', false, ['랍스터 요리', '장어구이', '한우 구이', '전복죽', '대게 찜', '훈제 오리']],
  ]),
];

/** 전체 세부 메뉴 수 */
export function countMenus(): number {
  return MAIN_CATEGORIES.reduce((sum, main) => sum + main.subCategories.reduce((s, sub) => s + sub.menus.length, 0), 0);
}

export function findMainCategory(id: string): MainCategory | undefined {
  return MAIN_CATEGORIES.find((c) => c.id === id);
}

export function findSubCategory(mainId: string, subId: string): SubCategory | undefined {
  return findMainCategory(mainId)?.subCategories.find((s) => s.id === subId);
}

/** 3단계에서 게임이 끝나는(가게가 정해지는) 중분류 목록 */
export function venueDecidingSubCategories(): SubCategory[] {
  return MAIN_CATEGORIES.flatMap((m) => m.subCategories.filter((s) => s.decidesVenue));
}
